import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { ApiAuthClient } from './auth/ApiAuthClient';
import { ApiCognitoAuthClient } from './auth/ApiCognitoAuthClient';
import { AuthProvider } from './auth/AuthProvider';
import { CognitoAuthClient } from './auth/CognitoAuthClient';
import { COGNITO_CONFIGURED, getCognitoConfig } from './auth/cognitoConfig';
import { API_CONFIGURED } from './data/apiBase';
import { PlanDataProvider } from './data/PlanDataProvider';
import { HouseholdDataProvider } from './data/household/HouseholdDataProvider';
import { router } from './router';
import { installStaleDeployRecovery } from './staleDeploy';
import './styles/index.css';

// A tab left open across a deploy holds chunk URLs the CDN no longer serves.
// Recover with a single reload before anything tries to render (see staleDeploy).
installStaleDeployRecovery(window);

// Explicit opt-in for the fenced dev-auth demo (mirrors the API's DEV_AUTH). The
// token-only, single-step ApiAuthClient is ONLY correct against a dev API that
// has no Cognito; it must be asked for, never inferred — see makeAuthClient.
const DEV_AUTH_ENABLED: boolean = import.meta.env.VITE_DEV_AUTH === 'true';

// Auth seam selection (all behind the same IAuthClient):
//  1. Cognito configured (VITE_COGNITO_*) AND an API URL → the server-MEDIATED
//     Cognito login (web → API → Cognito). This is the deployed topology and
//     the path whose out-of-repo drift we replaced with in-repo code.
//  2. Cognito configured but NO API URL → the browser-direct SRP TOTP flow.
//  3. No API URL at all → the fixture auth + offline seam (dev/demo).
//  4. API URL but NO Cognito → ONLY the explicitly-opted-in dev-auth demo
//     (VITE_DEV_AUTH=true) may use the dev-API ApiAuthClient. Otherwise this is
//     a MISCONFIGURED deploy — a real (Cognito) build whose VITE_COGNITO_* vars
//     went missing — and we FAIL CLOSED rather than silently shipping the
//     token-only single-step login, which cannot answer a Cognito MFA challenge
//     and looks like a working sign-in while being fundamentally broken.
function makeAuthClient(): ApiAuthClient | ApiCognitoAuthClient | CognitoAuthClient | undefined {
  const cognito = COGNITO_CONFIGURED ? getCognitoConfig() : null;
  if (cognito) return API_CONFIGURED ? new ApiCognitoAuthClient() : new CognitoAuthClient(cognito);

  // No Cognito config. With no API either, this is the offline fixture seam.
  if (!API_CONFIGURED) return undefined;

  // API IS configured but Cognito is NOT. The only legitimate reason is the
  // fenced dev-auth demo, which must opt in explicitly.
  if (DEV_AUTH_ENABLED) return new ApiAuthClient();

  throw new Error(
    'Auth misconfigured: VITE_API_URL is set but VITE_COGNITO_USER_POOL_ID / ' +
      'VITE_COGNITO_CLIENT_ID are missing, so the Cognito login cannot be used. ' +
      'A deployed build MUST set both Cognito vars. (For the fenced dev-auth demo ' +
      'only, set VITE_DEV_AUTH=true to use the dev-API login instead.)',
  );
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root not found');
}

let authClient;
try {
  authClient = makeAuthClient();
} catch (err) {
  // Fail closed AND visibly: a misconfigured auth build (see makeAuthClient)
  // must not render the app, but a blank page hides the cause. Surface the
  // reason in #root so a broken deploy is obvious in the browser, not just the
  // console, then rethrow to keep it loud for error reporting.
  container.textContent = err instanceof Error ? err.message : 'Authentication is misconfigured.';
  throw err;
}

const queryClient = new QueryClient();

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider client={authClient}>
        <PlanDataProvider>
          <HouseholdDataProvider>
            <RouterProvider router={router} />
          </HouseholdDataProvider>
        </PlanDataProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
